import { useSyncExternalStore } from "react";
import type { FeedbackTrigger } from "../../api/feedback";
import MiniEvent from "../../lib/mini-event";

/**
 * Which feedback moment has fired in this tab, if any.
 *
 * Module-level and in-memory, mirroring `liveStore.ts`: the trigger is a
 * transient event of the current visit, not something to persist or share. Call
 * sites scattered across the app (`ShowroomForm`, the user tour, …) announce a
 * moment with {@link setFeedbackTrigger}; the single `<FeedbackFormHost/>` in
 * the SparkShell reads it and decides whether a form matches.
 */
type State = { trigger: FeedbackTrigger | null };

const EMPTY: State = { trigger: null };

let state: State = EMPTY;
const event = new MiniEvent();

const store = {
  getState: () => state,
  subscribe: (listener: () => void) => event.addListener(listener),
};

/**
 * Announce that a feedback moment has occurred.
 *
 * The FIRST trigger of a visit wins: two moments can plausibly coincide (a fresh
 * registration landing straight in the Lernreise it just created), and only one
 * toast is ever shown, so later triggers are ignored until the host clears the
 * current one.
 */
export const setFeedbackTrigger = (trigger: FeedbackTrigger) => {
  if (state.trigger) return;
  state = { trigger };
  event.emit();
};

export const clearFeedbackTrigger = () => {
  if (!state.trigger) return;
  state = EMPTY;
  event.emit();
};

export const useFeedbackTrigger = (): FeedbackTrigger | null =>
  useSyncExternalStore(store.subscribe, store.getState).trigger;
